import { useState } from "react";
import axios from "axios";

const initialNewUserForm = {
  email: "",
  password: "",
  name: "",
  phone: "",
  customer_billing_type: "monthly",
  receipt_addressee: "",
  receipt_suffix: "様",
  receipt_include_address: false,
  receipt_address: "",
  signboard_header: "",
  signboard_watermark_data: "",
  signboard_watermark_mime_type: "",
  signboard_watermark_filename: "",
  signboard_watermark_opacity: 0.16,
  signboard_watermark_scale: 1,
};

const initialPasswordForm = {
  newPassword: "",
};

const initialUserProfileForm = {
  email: "",
  name: "",
  phone: "",
  customer_code: "",
  customer_billing_type: "monthly",
  receipt_addressee: "",
  receipt_suffix: "様",
  receipt_include_address: false,
  receipt_address: "",
  signboard_header: "",
  signboard_watermark_data: "",
  signboard_watermark_mime_type: "",
  signboard_watermark_filename: "",
  signboard_watermark_opacity: 0.16,
  signboard_watermark_scale: 1,
};

export default function useAdminUsers({ apiBaseUrl, getErrorMessage, setLoading }) {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const [showUserProfileForm, setShowUserProfileForm] = useState(false);
  const [selectedUserForAction, setSelectedUserForAction] = useState(null);
  const [showDeletedUsers, setShowDeletedUsers] = useState(false);
  const [newUserForm, setNewUserForm] = useState(initialNewUserForm);
  const [newPasswordForm, setNewPasswordForm] = useState(initialPasswordForm);
  const [userProfileForm, setUserProfileForm] = useState(initialUserProfileForm);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/users/pending`);
      setPendingUsers(response.data);
    } catch (error) {
      console.error("获取待审核用户失败:", error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/users?include_deleted=${showDeletedUsers}`);
      setAllUsers(response.data);
    } catch (error) {
      console.error("获取用户列表失败:", error);
    }
  };

  const handleBatchDeleteUsers = async () => {
    if (selectedUserIds.length === 0) {
      alert("请先选择要删除的用户");
      return;
    }

    if (!window.confirm(`确定要删除选中的 ${selectedUserIds.length} 个用户吗？此操作不可恢复！`)) {
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;

      for (const userId of selectedUserIds) {
        try {
          await axios.put(`${apiBaseUrl}/admin/users/${userId}/status`, { status: "deleted" });
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`删除用户 ${userId} 失败:`, err);
        }
      }

      if (errorCount > 0) {
        alert(`删除完成: ${successCount} 成功, ${errorCount} 失败`);
      } else {
        alert(`成功删除 ${successCount} 个用户`);
      }

      setSelectedUserIds([]);
      fetchAllUsers();
    } catch (error) {
      alert("批量删除失败: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    const selectableUsers = allUsers.filter(u => u.role !== "admin" && u.status !== "deleted");
    if (selectedUserIds.length === selectableUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(selectableUsers.map(u => u.id));
    }
  };

  const handleApproveUser = async (userId, status) => {
    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/users/approve`, {
        user_id: userId,
        status,
      });
      alert(`用户已${status === "approved" ? "批准" : status === "suspended" ? "暂停" : "拒绝"}`);
      fetchPendingUsers();
      fetchAllUsers();
    } catch (error) {
      alert("操作失败");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/users/create`, newUserForm);
      alert("用户创建成功！");
      setShowCreateUserForm(false);
      setNewUserForm(initialNewUserForm);
      fetchAllUsers();
    } catch (error) {
      alert(getErrorMessage(error, "创建用户失败"));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserStatus = async (userId, newStatus) => {
    const confirmMsg = {
      approved: "确定要启用此账号吗？",
      suspended: "确定要暂停此账号吗？暂停后用户将无法登录。",
      deleted: "确定要删除此账号吗？此操作不可撤销。"
    };

    if (!window.confirm(confirmMsg[newStatus])) return;

    setLoading(true);
    try {
      await axios.put(`${apiBaseUrl}/admin/users/${userId}/status`, { status: newStatus });
      const statusMsg = {
        approved: "账号已启用",
        suspended: "账号已暂停",
        deleted: "账号已删除"
      };
      alert(statusMsg[newStatus]);
      fetchAllUsers();
      fetchPendingUsers();
    } catch (error) {
      alert(getErrorMessage(error, "操作失败"));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserChatSettings = async (userId, chatEnabled) => {
    const confirmMessage = chatEnabled
      ? "确定要为此用户启用订单聊天室吗？"
      : "确定要为此用户禁用订单聊天室吗？禁用后该用户订单不会在后台和司机端显示聊天室链接。";
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    try {
      await axios.put(`${apiBaseUrl}/admin/users/${userId}/chat-settings`, {
        chat_enabled: chatEnabled,
      });
      alert(chatEnabled ? "订单聊天室已启用" : "订单聊天室已禁用");
      fetchAllUsers();
    } catch (error) {
      alert(getErrorMessage(error, "聊天室设置更新失败"));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRetailMemberBindingSettings = async (userId, enabled) => {
    const confirmMessage = enabled
      ? "确定要让此账号的历史订单参与会员手机号自动绑定吗？"
      : "确定要让此账号的历史订单不参与会员手机号自动绑定吗？关闭后，该账号名下订单不会自动进入散客会员历史订单。";
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    try {
      await axios.put(`${apiBaseUrl}/admin/users/${userId}/retail-member-binding-settings`, {
        retail_member_order_binding_enabled: enabled,
      });
      alert(enabled ? "会员历史订单绑定已启用" : "会员历史订单绑定已关闭");
      fetchAllUsers();
    } catch (error) {
      alert(getErrorMessage(error, "会员历史订单绑定设置更新失败"));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPasswordForm.newPassword.length < 6) {
      alert("密码长度至少为6个字符");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/users/${selectedUserForAction.id}/reset-password`, {
        new_password: newPasswordForm.newPassword
      });
      alert("密码重置成功！");
      closeResetPasswordForm();
    } catch (error) {
      alert(getErrorMessage(error, "重置密码失败"));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserProfile = async (e) => {
    e.preventDefault();
    if (!selectedUserForAction) return;

    setLoading(true);
    try {
      await axios.put(`${apiBaseUrl}/admin/users/${selectedUserForAction.id}/profile`, userProfileForm);
      alert("ユーザー情報を保存しました");
      closeUserProfileForm();
      fetchAllUsers();
    } catch (error) {
      alert(getErrorMessage(error, "ユーザー情報の保存に失敗しました"));
    } finally {
      setLoading(false);
    }
  };

  const openResetPasswordForm = (selectedUser) => {
    setSelectedUserForAction(selectedUser);
    setShowResetPasswordForm(true);
  };

  const openUserProfileForm = async (selectedUser) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/users/${selectedUser.id}/profile`);
      selectedUser = response.data;
    } catch (error) {
      alert(getErrorMessage(error, "ユーザー情報の取得に失敗しました"));
      setLoading(false);
      return;
    }
    setLoading(false);
    setSelectedUserForAction(selectedUser);
    setUserProfileForm({
      email: selectedUser.email || "",
      name: selectedUser.name || "",
      phone: selectedUser.phone || "",
      customer_code: selectedUser.customer_code || "",
      customer_billing_type: selectedUser.customer_billing_type || "monthly",
      receipt_addressee: selectedUser.receipt_addressee || "",
      receipt_suffix: selectedUser.receipt_suffix || "様",
      receipt_include_address: selectedUser.receipt_include_address || false,
      receipt_address: selectedUser.receipt_address || "",
      signboard_header: selectedUser.signboard_header || "",
      signboard_watermark_data: selectedUser.signboard_watermark_data || "",
      signboard_watermark_mime_type: selectedUser.signboard_watermark_mime_type || "",
      signboard_watermark_filename: selectedUser.signboard_watermark_filename || "",
      signboard_watermark_opacity: selectedUser.signboard_watermark_opacity ?? 0.16,
      signboard_watermark_scale: selectedUser.signboard_watermark_scale ?? 1,
    });
    setShowUserProfileForm(true);
  };

  const closeResetPasswordForm = () => {
    setShowResetPasswordForm(false);
    setSelectedUserForAction(null);
    setNewPasswordForm(initialPasswordForm);
  };

  const closeUserProfileForm = () => {
    setShowUserProfileForm(false);
    setSelectedUserForAction(null);
    setUserProfileForm(initialUserProfileForm);
  };

  return {
    allUsers,
    closeResetPasswordForm,
    closeUserProfileForm,
    fetchAllUsers,
    fetchPendingUsers,
    handleApproveUser,
    handleBatchDeleteUsers,
    handleCreateUser,
    handleResetPassword,
    handleUpdateUserProfile,
    handleUpdateUserChatSettings,
    handleUpdateUserRetailMemberBindingSettings,
    handleUpdateUserStatus,
    newPasswordForm,
    newUserForm,
    openResetPasswordForm,
    openUserProfileForm,
    pendingUsers,
    selectedUserForAction,
    selectedUserIds,
    setNewPasswordForm,
    setNewUserForm,
    setUserProfileForm,
    setShowCreateUserForm,
    setShowDeletedUsers,
    showCreateUserForm,
    showDeletedUsers,
    showResetPasswordForm,
    showUserProfileForm,
    toggleAllUsers,
    toggleUserSelection,
    userProfileForm,
  };
}
